import { useEffect, useState } from "react";
import { TrendingUp, PartyPopper } from "lucide-react";
import api from "@/lib/api";

/**
 * Positive-outcome card. Shows people who signed up to volunteer AFTER receiving campaign emails.
 * These aren't unsubscribes — they're wins. Great for board reports and ROI evidence.
 */
export default function CampaignConversionsCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetch_ = async () => {
    try {
      const r = await api.get("/admin/campaign-conversions");
      setData(r.data);
    } catch { setData(null); }
  };

  useEffect(() => {
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const rows = data.conversions || [];
  const cap = expanded ? rows.length : 5;
  const visible = rows.slice(0, cap);

  const rate = data.emailed_contacts > 0
    ? ((rows.length / data.emailed_contacts) * 100).toFixed(1)
    : "0.0";

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div data-testid="conversions-card" className="rounded-2xl border border-amber-100 bg-white shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-amber-50 bg-gradient-to-r from-amber-50/80 via-white to-emerald-50/40">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-amber-300 text-emerald-950 flex items-center justify-center">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div>
            <div className="font-[Outfit] text-[11px] uppercase tracking-[0.18em] text-amber-800 font-bold">Campaign Conversions</div>
            <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">
              <span className="tabular-nums text-rose-600 font-semibold">{rows.length}</span> {rows.length === 1 ? "person" : "people"} signed up after receiving campaign emails
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-[Outfit] text-[10px] uppercase tracking-[0.18em] text-emerald-900/60 font-bold">Conversion rate</div>
            <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 tabular-nums">{rate}%</div>
          </div>
          {rows.length > 5 && (
            <button data-testid="conversions-toggle" onClick={() => setExpanded((v) => !v)} className="text-[11px] font-[Outfit] font-semibold text-emerald-700 hover:text-emerald-900">
              {expanded ? "Show less" : `Show all ${rows.length}`}
            </button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-4 text-sm text-emerald-800/60 font-[Outfit] italic flex items-center gap-2">
          <PartyPopper className="w-4 h-4 text-amber-500" />
          No conversions yet — signups will appear here as the campaign fires more emails.
        </div>
      ) : (
        <ul className="divide-y divide-amber-50">
          {visible.map((c, i) => (
            <li key={c.email + i} data-testid={`conversion-row-${i}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-amber-50/30">
              <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 text-emerald-800 font-bold text-xs">
                #{i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-[Outfit] text-sm font-semibold text-emerald-950 truncate">
                  {c.name || c.email}
                </div>
                <div className="font-[Outfit] text-[11px] text-emerald-900/60 truncate">{c.email}</div>
              </div>
              <div className="hidden sm:block text-right shrink-0">
                <div className="font-[Outfit] text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Received</div>
                <div className="font-[Outfit] text-xs text-emerald-900 tabular-nums">
                  {c.emails_received_before_conversion} {c.emails_received_before_conversion === 1 ? "email" : "emails"}
                </div>
              </div>
              <div className="text-right shrink-0 min-w-[80px]">
                <div className="font-[Outfit] text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Signed up</div>
                <div className="font-[Outfit] text-xs text-emerald-900 tabular-nums whitespace-nowrap">{fmt(c.converted_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
